import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useAuth } from "@/modules/authentication/context/AuthContext";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[4px] text-sm font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-white shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "bg-transparent hover:bg-secondary/80 hover:text-secondary-foreground dark:hover:bg-secondary/50",
        link: "text-primary underline-offset-4 hover:underline",
        black: "bg-black text-white hover:bg-black/90",
        livekit:
          "bg-gray-900 hover:bg-gray-700 hover:text-secondary-foreground dark:hover:bg-secondary/50 rounded-full text-[14px]",
        livekitDestructive:
          "bg-[#BE123C] text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 rounded-full text-[14px]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-[4px] gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-sm px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  permissionCode,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    permissionCode?: string | string[];
  }) {
  const Comp = asChild ? Slot : "button";
  const { user } = useAuth();

  const checkPermission = (code: string): boolean => {
    if (user?.type === "admin") return true;
    if (user?.type === "agency") return true;
    if (!user) return false;
    if (user?.userPermission && user.userPermission.length > 0) {
      return user.userPermission.some(
        (userPerm) => userPerm.permission?.code === code
      );
    }
    return false;
  };

  let hasPermission = false;
  if (Array.isArray(permissionCode)) {
    hasPermission = permissionCode.some((code) => checkPermission(code));
  } else {
    hasPermission = checkPermission(permissionCode ?? "");
  }
  if (!hasPermission && permissionCode) return null;

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
export { Button, buttonVariants };
